import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import type {AddressComparisonFields} from "@mytour/shared";
import {Button} from "reactstrap";
import {
  type StructuredReplacementFields,
  normalizeAddressComponent,
} from "../useAddressVerification.js";
import {
  DIFF_MARK_STYLE,
  RESOLVED_CARD_TINT,
  RESOLVED_ICON_TINT,
  STATUS_FULL_WIDTH_BUTTON_STYLE,
  STATUS_ICON_BASE_STYLE,
  STATUS_LABEL_WRAP_STYLE,
  STATUS_TITLE_STYLE,
} from "./statusCardStyles.js";

type Props = {
  /** Saisie courante — comparée composant par composant au résolu pour décider quel segment surligner. */
  fields: AddressComparisonFields;
  /** Rue/CP/ville résolus par l'IGN — voir `AddressVerification.structuredReplacement`. Toujours non-`null` ici (gate posé par le dispatcher). */
  replacement: StructuredReplacementFields;
  /** Absent = pas de bouton « Remplacer » — voir `AddressGeocodeStatus`. */
  onReplace?: () => void;
  /** Bascule en mode pin (`verification.openMap`) — toujours disponible depuis cet état, contrairement à `onReplace` (mock-up validé, cadre 5). */
  onPositionOnMap: () => void;
};

/**
 * État `status: "close-match"` (#980, étendu aux lieux-dits par #1082) :
 * precision `housenumber`/`street`/`locality`/`toponym` mais divergente de la
 * saisie — callout « Adresse proche trouvée », bouton « Remplacer » primaire
 * intégré, « Positionner sur la carte » secondaire sous le callout (mock-up
 * validé, cadre 5). « Remplacer » ne soumet jamais, ne fait que réécrire les
 * champs et relancer la vérification (le hook re-géocode déjà sur tout
 * changement de `fields`).
 */
export function AddressCloseMatch({fields, replacement, onReplace, onPositionOnMap}: Props) {
  const streetDiffers =
    normalizeAddressComponent(replacement.line1) !== normalizeAddressComponent(fields.line1);
  const zipDiffers =
    normalizeAddressComponent(replacement.zip) !== normalizeAddressComponent(fields.zip);
  const cityDiffers =
    normalizeAddressComponent(replacement.city) !== normalizeAddressComponent(fields.city);

  return (
    <>
      <div style={{...cardStyle, ...RESOLVED_CARD_TINT}}>
        <div style={{display: "flex", alignItems: "flex-start", gap: "10px"}}>
          <span style={{...STATUS_ICON_BASE_STYLE, ...RESOLVED_ICON_TINT}}>
            <FontAwesomeIcon icon="map-marker-alt" />
          </span>
          <div style={{minWidth: 0}}>
            <div style={STATUS_TITLE_STYLE}>Adresse proche trouvée</div>
            <div style={STATUS_LABEL_WRAP_STYLE}>
              {streetDiffers ? (
                <mark style={DIFF_MARK_STYLE}>{replacement.line1}</mark>
              ) : (
                replacement.line1
              )}
              {", "}
              {zipDiffers ? (
                <mark style={DIFF_MARK_STYLE}>{replacement.zip}</mark>
              ) : (
                replacement.zip
              )}{" "}
              {cityDiffers ? (
                <mark style={DIFF_MARK_STYLE}>{replacement.city}</mark>
              ) : (
                replacement.city
              )}
            </div>
          </div>
        </div>
        {onReplace && (
          <Button
            color="primary"
            size="sm"
            type="button"
            style={STATUS_FULL_WIDTH_BUTTON_STYLE}
            onClick={onReplace}
          >
            Remplacer
          </Button>
        )}
      </div>
      <Button
        color="primary"
        outline
        size="sm"
        type="button"
        style={STATUS_FULL_WIDTH_BUTTON_STYLE}
        onClick={onPositionOnMap}
      >
        Positionner sur la carte
      </Button>
    </>
  );
}

// Un peu plus généreux que `STATUS_CARD_BASE_STYLE` (padding 12px/14px) pour laisser respirer le bouton pleine largeur — mock-up validé, cadre 5.
const cardStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  borderRadius: "12px",
  padding: "12px 14px",
  marginBottom: "8px",
} as const;
